/**
 * Development Tools - Utilities for testing and debugging
 *
 * Access these functions in browser console via window.devTools
 */

import {
  clearSymbolsCache,
  getCachedSymbols,
  isCacheFresh,
} from "./symbolsCache";
import { useAuthStore } from "../store/authStore";
import { getAutoSaveKey } from "../workspace/constants";
import marketdata from "../data/Services/marketdata.jsx";
import useWatchlistStore from "../store/watchlistStore";

// ── Chart-data export helpers (used by devTools.exportChartData) ──────────────

// Poll window.__PLOTTED__[chartId] until the useChartData tap fills it after a
// forced recompute, or until timeout. Returns the captured rows or null.
function waitForPlotted(chartId, timeoutMs = 2000) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const rows = window.__PLOTTED__ && window.__PLOTTED__[chartId];
      if (rows) return resolve(rows);
      if (Date.now() - start >= timeoutMs) return resolve(null);
      setTimeout(tick, 30);
    };
    tick();
  });
}

// Normalize a single field value for export: Date -> ISO, {volume} wrappers
// (volume/financialVolume/tickCount carry an object after the volume indicator)
// -> the inner number, other objects -> shallow JSON, primitives untouched.
function normalizeValue(v) {
  if (v == null) return v;
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "object") {
    if (typeof v.volume === "number") return v.volume;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return v;
}

// Flatten plotted rows to CSV over the union of all keys (skips functions).
function rowsToCSV(rows) {
  const keys = [];
  const seen = new Set();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (typeof row[k] === "function") continue;
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  const escape = (val) => {
    const s = val == null ? "" : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = keys.join(",");
  const lines = rows.map((row) =>
    keys.map((k) => escape(normalizeValue(row[k]))).join(","),
  );
  return [header, ...lines].join("\n");
}

function downloadTextFile(filename, text, mime) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const devTools = {
  /**
   * Clear all caches and storage (symbols cache + localStorage)
   */
  clearAll: async () => {
    console.log("🧹 Clearing all caches...");

    // Clear IndexedDB symbols cache
    await clearSymbolsCache();
    console.log("✅ Symbols cache cleared");

    // Clear localStorage
    localStorage.clear();
    console.log("✅ localStorage cleared");

    // Clear sessionStorage
    sessionStorage.clear();
    console.log("✅ sessionStorage cleared");

    console.log("✅ All caches cleared! Reload to test fresh startup.");
  },

  /**
   * Clear only symbols cache (keep localStorage/workspace data)
   */
  clearSymbolsOnly: async () => {
    console.log("🧹 Clearing symbols cache...");
    await clearSymbolsCache();
    console.log(
      "✅ Symbols cache cleared! Reload to test first-time symbols load.",
    );
  },

  /**
   * Show cache status and information
   */
  cacheInfo: async () => {
    console.log("📊 Cache Information:");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    const cached = await getCachedSymbols();

    if (!cached) {
      console.log("❌ No cache found");
      return;
    }

    const isFresh = await isCacheFresh();
    const age = Date.now() - cached.timestamp;
    const ageMinutes = Math.round(age / 1000 / 60);
    const ageHours = (age / 1000 / 60 / 60).toFixed(1);
    const sizeKB = (JSON.stringify(cached.data).length / 1024).toFixed(2);
    const sizeMB = (sizeKB / 1024).toFixed(2);

    console.log(`✅ Cache Status: ${isFresh ? "FRESH" : "STALE"}`);
    console.log(`📅 Cached: ${new Date(cached.timestamp).toLocaleString()}`);
    console.log(`⏰ Age: ${ageMinutes} minutes (${ageHours} hours)`);
    console.log(`📦 Symbols: ${cached.data.length} items`);
    console.log(`💾 Size: ${sizeMB} MB (${sizeKB} KB)`);
    console.log(
      `🔄 Valid until: ${new Date(cached.timestamp + 24 * 60 * 60 * 1000).toLocaleString()}`,
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  },

  /**
   * Clear cache and reload page in one command
   */
  freshStart: async () => {
    console.log("🔄 Clearing cache and reloading...");
    await clearSymbolsCache();
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  },

  /**
   * Clear only auto-saved workspace (keep symbols cache)
   * Clears all user-scoped workspace and settings keys
   */
  clearWorkspace: () => {
    console.log("🧹 Clearing user-scoped data (all users/brokers)...");
    console.log(
      "💡 Note: Auto-save keys follow pattern: odin-sight-workspace-autosave-{BROKER}-{USER}",
    );
    // Clear all user-scoped keys from localStorage
    const keyPrefixes = [
      "odin-sight-workspace-autosave",
      "workspace-layout",
      "odin-settings",
      "sidebar-favorites",
      "favorite-indicators",
      "drawing-color-storage",
      "theme",
      "odin-news-storage",
      "chart-preference",
      "objects-view-mode",
      "sidebar-view-mode",
      "objects-selected-category",
    ];

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && keyPrefixes.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    console.log(
      `✅ Cleared ${keysToRemove.length} user-scoped keys! Reload to start fresh.`,
    );
  },

  /**
   * Factory Reset - Complete reset simulating first-time user
   * Clears all storage WITHOUT auto-saving current state
   */
  factoryReset: async () => {
    console.log("🏭 FACTORY RESET - Simulating first-time user...");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    // Set flag to prevent auto-save on beforeunload
    sessionStorage.setItem("skip-autosave", "true");
    console.log("⏸️  Auto-save disabled for this session");

    // Clear IndexedDB symbols cache
    await clearSymbolsCache();
    console.log("✅ Symbols cache cleared");

    // Clear all localStorage (all Zustand stores, workspace, settings, What's New, etc.)
    localStorage.clear();
    console.log(
      "✅ localStorage cleared (all stores, workspace, settings, What's New)",
    );

    // Clear sessionStorage (except our skip-autosave flag)
    const skipFlag = sessionStorage.getItem("skip-autosave");
    sessionStorage.clear();
    sessionStorage.setItem("skip-autosave", skipFlag);
    console.log("✅ sessionStorage cleared");
    console.log("✅ What's New state cleared (will auto-open after reload)");

    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("🔄 Reloading to simulate first-time user experience...");

    // Reload without saving state
    setTimeout(() => {
      window.location.reload();
    }, 500);
  },

  /**
   * What's New - Reset to see panel again
   */
  resetWhatsNew: () => {
    console.log("✨ Resetting What's New state...");
    localStorage.removeItem("odin_whats_new");

    // Also clear cached HTML
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("odin_whats_new_cache")) {
        localStorage.removeItem(key);
      }
    });

    console.log("✅ What's New reset! Reload to see panel again.");
    console.log("💡 Tip: Panel will auto-open after 3 seconds (post-login)");
  },

  /**
   * What's New - Simulate version change
   */
  simulateVersionChange: (oldVersion = "1.0.0") => {
    console.log(`✨ Simulating version change: ${oldVersion} → current`);
    localStorage.setItem(
      "odin_whats_new",
      JSON.stringify({
        lastSeenVersion: oldVersion,
        lastSeenDate: new Date().toISOString(),
      }),
    );
    console.log("✅ State updated! Reload to see What's New panel.");
  },

  /**
   * Tip of the Day - Reset and show immediately
   * Clears the once-per-session flag + rotation index, then shows a tip now
   * (bypasses the noPopups / session gates via the tipOfTheDay:show event).
   */
  resetTipOfTheDay: () => {
    console.log("💡 Resetting Tip of the Day...");
    sessionStorage.removeItem("odin_tip_shown");
    localStorage.removeItem("odin_tip_of_the_day");
    window.dispatchEvent(new Event("tipOfTheDay:show"));
    console.log(
      "✅ Tip of the Day reset — showing now (rotation back to the first tip).",
    );
    console.log("💡 It will also auto-show again on the next session.");
  },

  /**
   * Auto-Save - Manually trigger restore (for testing)
   */
  triggerRestore: () => {
    console.log("🔧 Manually triggering restore...");
    try {
      const authState = useAuthStore.getState();
      const user = authState.getUsername();
      const broker = authState.getBroker();

      if (!user || !broker) {
        console.error("❌ Cannot restore: user/broker not available");
        return;
      }

      // Check if auto-save data exists
      const autoSaveKey = getAutoSaveKey(user, broker);
      const data = localStorage.getItem(autoSaveKey);

      if (!data) {
        console.error("❌ No auto-save data found to restore");
        console.log("💡 Run devTools.triggerAutoSave() first");
        return;
      }

      // Get charts store
      if (!window.__CHARTS_STORE__) {
        console.error("❌ Cannot restore: charts store not available");
        return;
      }

      const chartsStore = window.__CHARTS_STORE__;

      // Import and call restore
      import("../workspace")
        .then(({ loadAutoSavedWorkspace }) => {
          console.log("📦 Calling loadAutoSavedWorkspace...");
          const success = loadAutoSavedWorkspace(
            chartsStore.loadWorkspace,
            user,
            broker,
          );
          if (success) {
            console.log("✅ Restore triggered successfully!");
          } else {
            console.error("❌ Restore failed");
          }
        })
        .catch((error) => {
          console.error("❌ Failed to import workspace module:", error);
        });
    } catch (error) {
      console.error("❌ Error triggering restore:", error);
    }
  },

  /**
   * Auto-Save - Manually trigger auto-save (for testing)
   */
  triggerAutoSave: () => {
    console.log("🔧 Manually triggering auto-save...");
    try {
      const authState = useAuthStore.getState();
      const user = authState.getUsername();
      const broker = authState.getBroker();

      if (!user || !broker) {
        console.error("❌ Cannot auto-save: user/broker not available");
        console.log("   User:", user);
        console.log("   Broker:", broker);
        return;
      }

      // Get charts store
      if (!window.__CHARTS_STORE__) {
        console.error("❌ Cannot auto-save: charts store not available");
        return;
      }

      const chartsStore = window.__CHARTS_STORE__;
      console.log("📊 Charts store state:");
      console.log("   Charts count:", chartsStore.charts?.length || 0);
      console.log(
        "   Desktop name:",
        window.useWorkspaceStore?.getState()?.desktopName || "unknown",
      );

      if (!chartsStore.charts || chartsStore.charts.length === 0) {
        console.warn("⚠️  No charts to save");
        return;
      }

      // Import and call autoSaveWorkspace
      import("../workspace")
        .then(({ autoSaveWorkspace }) => {
          const success = autoSaveWorkspace(chartsStore, user, broker);
          if (success) {
            console.log("✅ Auto-save triggered successfully!");
            console.log("💡 Run devTools.autoSaveInfo() to verify");
          } else {
            console.error("❌ Auto-save failed");
          }
        })
        .catch((error) => {
          console.error("❌ Failed to import workspace module:", error);
        });
    } catch (error) {
      console.error("❌ Error triggering auto-save:", error);
    }
  },

  /**
   * Auto-Save - Show current auto-save state
   */
  autoSaveInfo: () => {
    console.log("💾 Auto-Save State:");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    // Get current user/broker from auth store
    let user = "not-logged-in";
    let broker = "no-broker";
    try {
      const authState = useAuthStore.getState();
      user = authState.getUsername() || "not-logged-in";
      broker = authState.getBroker() || "no-broker";
    } catch (error) {
      console.warn("Could not get user/broker:", error);
    }

    console.log(`👤 User: ${user}`);
    console.log(`🏢 Broker: ${broker}`);

    // List all workspace auto-save keys in localStorage
    console.log("");
    console.log("📦 All workspace auto-save keys in localStorage:");
    const allAutoSaveKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("odin-sight-workspace-autosave")) {
        allAutoSaveKeys.push(key);
      }
    }

    if (allAutoSaveKeys.length === 0) {
      console.log("   ❌ No auto-save keys found at all!");
    } else {
      console.log(`   Found ${allAutoSaveKeys.length} auto-save key(s):`);
      allAutoSaveKeys.forEach((key) => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          const savedDate = new Date(data.timestamp);
          const age = Date.now() - savedDate.getTime();
          const ageMinutes = Math.round(age / 1000 / 60);
          console.log(`   ✅ ${key}`);
          console.log(`      Desktop: "${data.desktopName || "NO NAME"}"`);
          console.log(`      Charts: ${data.charts?.length || 0}`);
          console.log(`      Age: ${ageMinutes} minutes`);
        } catch {
          console.log(`   ⚠️  ${key} (parsing failed)`);
        }
      });
    }

    console.log("");
    console.log("🔍 Checking expected key for current user/broker:");

    // Check for auto-saved workspace with computed key (using proper function)
    const autoSaveKey = getAutoSaveKey(user, broker);
    const autoSaved = localStorage.getItem(autoSaveKey);

    console.log(`   Expected key: ${autoSaveKey}`);

    if (!autoSaved) {
      console.log(
        "   ❌ No auto-saved workspace found for current user/broker",
      );
    } else {
      try {
        const parsed = JSON.parse(autoSaved);
        const savedDate = new Date(parsed.timestamp);
        const age = Date.now() - savedDate.getTime();
        const ageMinutes = Math.round(age / 1000 / 60);
        const ageHours = (age / 1000 / 60 / 60).toFixed(1);

        console.log("   ✅ Auto-saved workspace found:");
        console.log(`      Desktop: "${parsed.desktopName || "NO NAME"}"`);
        console.log(`      Charts: ${parsed.charts?.length || 0}`);
        console.log(`      Version: ${parsed.version}`);
        console.log(`      Saved: ${savedDate.toLocaleString()}`);
        console.log(`      Age: ${ageMinutes} minutes (${ageHours} hours)`);
        console.log(
          `      Will restore: ${ageHours < 1 ? "YES" : "NO (too old)"}`,
        );

        if (parsed.charts?.length > 0) {
          console.log(
            `      First chart: ${parsed.charts[0].symbol} (${parsed.charts[0].periodicity})`,
          );
        }
      } catch (error) {
        console.error("   ❌ Failed to parse auto-save:", error);
      }
    }

    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  },

  /**
   * Auto-Save - Show last auto-save attempt (from sessionStorage)
   * Useful for debugging auto-save that happens during page navigation
   */
  lastAutoSaveAttempt: () => {
    console.log("🔍 Last Auto-Save Attempt:");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    const lastAttempt = sessionStorage.getItem("last-autosave-attempt");

    if (!lastAttempt) {
      console.log("❌ No last auto-save attempt found");
      console.log("💡 This gets logged when beforeunload/pagehide events fire");
      console.log("   Navigate away from page to trigger auto-save");
    } else {
      try {
        const parsed = JSON.parse(lastAttempt);
        const attemptDate = new Date(parsed.timestamp);
        const age = Date.now() - attemptDate.getTime();
        const ageMinutes = Math.round(age / 1000 / 60);

        console.log("✅ Last auto-save attempt found:");
        console.log(`   Timestamp: ${attemptDate.toLocaleString()}`);
        console.log(`   Age: ${ageMinutes} minutes`);
        console.log(`   Event: ${parsed.event}`);
        console.log(`   Success: ${parsed.success ?? "unknown"}`);

        if (parsed.success !== false) {
          console.log("");
          console.log("📊 Auto-save Details:");
          console.log(`   User: ${parsed.user}`);
          console.log(`   Broker: ${parsed.broker}`);
          console.log(`   Key: ${parsed.key}`);
          console.log(`   Desktop: "${parsed.desktopName}"`);
          console.log(`   Charts: ${parsed.chartsCount}`);

          if (parsed.charts && parsed.charts.length > 0) {
            console.log("");
            console.log("   Charts Saved:");
            parsed.charts.forEach((chart, idx) => {
              console.log(
                `      ${idx + 1}. ${chart.symbol} (${chart.periodicity}) - ${chart.indicators || 0} indicators`,
              );
            });
          }
        } else {
          console.log("");
          console.log(`❌ Error: ${parsed.error}`);
        }
      } catch (error) {
        console.error("❌ Failed to parse last attempt:", error);
      }
    }

    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  },

  /**
   * Quote Subscriptions - Show all registered symbols and their subscribers
   */
  quoteSubscriptions: () => {
    return marketdata.debugQuoteSubscriptions();
  },

  /**
   * Register/unregister a quote subscription (for E2E testing)
   */
  registerQuote: (symbol, subscriberId) => {
    marketdata.registerQuote(symbol, subscriberId);
  },
  unregisterQuote: (symbol, subscriberId) => {
    marketdata.unregisterQuote(symbol, subscriberId);
  },

  /**
   * Quote Health - Unified per-symbol health dashboard (subscriptions + worker state)
   * Shows staleness, subscribers, last ON (gap detection), and worker timing
   */
  quoteHealth: async () => {
    const subscriptions = marketdata.subscriptionManager?.debug() || [];
    const now = Date.now();
    const STALE_THRESHOLD = 30000;

    if (subscriptions.length === 0) {
      console.log("[QuoteHealth] No active subscriptions");
      return [];
    }

    // Fetch worker state in parallel
    const workerData = await marketdata.debugWorkerState();
    const workerGaps = workerData?.gapCounts || {};
    const workerMissed = workerData?.totalMissed || {};

    if (workerData) {
      console.log(
        `[QuoteHealth] Worker throttle: ${workerData.throttleInterval}ms`,
      );
    }

    const rows = subscriptions.map((sub) => {
      const symbol = sub.canonical || sub.symbol;
      const quote = marketdata.getLastQuote?.(symbol);
      const receivedAt = quote?._receivedAt;
      const age = receivedAt ? (now - receivedAt) / 1000 : null;
      const status = !receivedAt
        ? "❓ NO DATA"
        : age > STALE_THRESHOLD / 1000
          ? "⚠️ STALE"
          : "✅ OK";

      return {
        symbol,
        subs: sub.refCount,
        serverDT: quote?.DT ? new Date(quote.DT).toLocaleTimeString() : "-",
        received: receivedAt
          ? new Date(receivedAt).toLocaleTimeString()
          : "never",
        "age(s)": age !== null ? age.toFixed(1) : "-",
        gaps: workerGaps[symbol] || 0,
        missed: workerMissed[symbol] || 0,
        status,
      };
    });

    console.log("[QuoteHealth] Per-symbol status:");
    console.table(rows);
    return rows;
  },

  /**
   * Debug: inspect cached quote for a symbol
   */
  inspectSymbolCache: (symbol) => {
    const canonical = marketdata.getCanonicalSymbol?.(symbol) ?? symbol;
    const quote = marketdata.getLastQuote?.(symbol);
    return {
      canonical,
      lastPrice: quote?.C,
      quote: quote ? { ...quote } : null,
      hasQuote: !!quote,
    };
  },

  /**
   * Show full last quote for a symbol (all fields including ON/PON, daily data)
   * @param {string} symbol - Any symbol variant (e.g., "WINFUT", "WING26", "PETR4")
   */
  showLastQuote: (symbol) => {
    if (!symbol) {
      console.log('[showLastQuote] Usage: devTools.showLastQuote("WINFUT")');
      return null;
    }
    const quote = marketdata.getLastQuote?.(symbol);
    if (!quote) {
      console.log(`[showLastQuote] No data for "${symbol}"`);
      return null;
    }
    const canonical = marketdata.getCanonicalSymbol?.(symbol) ?? symbol;
    console.log(
      `[showLastQuote] ${symbol}${canonical !== symbol ? ` → ${canonical}` : ""}`,
    );
    console.table({
      "Close (C)": quote.C,
      "Daily Open (DO)": quote.DO,
      "Daily High (DH)": quote.DH,
      "Daily Low (DL)": quote.DL,
      "Daily Volume (DV)": quote.DV,
      "Prev Close (PC)": quote.PC,
      "VWAP (VW)": quote.VW,
      "Order # (ON)": quote.ON,
      "Prev Order # (PON)": quote.PON,
      "Type (TY)": quote.TY,
      "Server Time (DT)": quote.DT
        ? new Date(quote.DT).toLocaleTimeString()
        : "-",
      "Received At": quote._receivedAt
        ? new Date(quote._receivedAt).toLocaleTimeString()
        : "-",
      "Age (s)": quote._receivedAt
        ? ((Date.now() - quote._receivedAt) / 1000).toFixed(1)
        : "-",
    });
    return quote;
  },

  /**
   * Watchlist store access (for E2E testing)
   */
  watchlistStore: () => useWatchlistStore.getState(),
  watchlistSetState: (partial) => useWatchlistStore.setState(partial),

  /**
   * What's New - Show current state
   */
  whatsNewInfo: () => {
    console.log("✨ What's New State:");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    const state = localStorage.getItem("odin_whats_new");
    if (!state) {
      console.log("❌ No state found (first-time user)");
      console.log("💡 Panel WILL auto-open on reload");
    } else {
      const parsed = JSON.parse(state);
      console.log("✅ State:", parsed);
      console.log("💡 Panel will NOT auto-open (version already seen)");
    }
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  },

  /**
   * Show all available dev tools commands
   */
  /**
   * Export the fully-processed (plotted) chart data for a chartId — OHLC plus
   * every indicator, comparison and bar-color output, EXACTLY as rendered.
   *
   * Arms a window-gated tap in useChartData (zero cost when idle), forces one
   * recompute via nudgeChart (= a normal tick, works with the market closed),
   * reads the captured rows, optionally slices to the on-screen window, and
   * downloads JSON/CSV. The tap is disarmed and the capture cleared on exit.
   *
   * @param {string} [chartId]            defaults to the only open chart if omitted
   * @param {object} [opts]
   * @param {boolean} [opts.visibleOnly=true]  slice to the on-screen xExtents window
   * @param {'json'|'csv'} [opts.format='json']
   * @param {boolean} [opts.download=true]     download a file (false = just return rows)
   * @returns {Promise<Array|undefined>} the exported rows
   */
  /**
   * List the open charts (id, symbol, periodicity, bar/indicator counts, visible
   * window) — use the id with exportChartData. Returns the rows too.
   */
  charts: () => {
    const store = window.__CHARTS_STORE__;
    if (!store) {
      console.error("❌ Charts store not available (window.__CHARTS_STORE__)");
      return;
    }
    const rows = (store.charts || []).map((c) => ({
      id: c.id,
      symbol: c.symbol,
      periodicity: c.periodicity,
      bars: store.chartData?.[c.id]?.data?.length || 0,
      indicators: c.indicators?.length || 0,
      visible: Array.isArray(c.xExtents)
        ? `${Math.floor(c.xExtents[0])}–${Math.ceil(c.xExtents[1])}`
        : "—",
    }));
    if (rows.length === 0) console.warn("⚠️  No open charts");
    else console.table(rows);
    return rows;
  },

  exportChartData: async (chartId, opts = {}) => {
    const { visibleOnly = true, format = "json", download = true } = opts;
    const store = window.__CHARTS_STORE__;
    if (!store) {
      console.error("❌ Charts store not available (window.__CHARTS_STORE__)");
      return;
    }

    const charts = store.charts || [];
    let chart = charts.find((c) => c.id === chartId);
    if (!chart) {
      if (chartId == null && charts.length === 1) {
        chart = charts[0];
      } else {
        console.error(
          `❌ chartId "${chartId}" not found. Open charts:`,
          charts.map((c) => ({
            id: c.id,
            symbol: c.symbol,
            periodicity: c.periodicity,
          })),
        );
        return;
      }
    }
    const id = chart.id;

    // Arm the tap and force a fresh recompute so the capture reflects the
    // current screen even when no live quotes are flowing.
    window.__CAPTURE_PLOTTED__ = true;
    window.__PLOTTED__ = window.__PLOTTED__ || {};
    delete window.__PLOTTED__[id];
    try {
      if (typeof store.nudgeChart !== "function") {
        console.error(
          "❌ store.nudgeChart unavailable — cannot force recompute",
        );
        return;
      }
      store.nudgeChart(id);

      const rows = await waitForPlotted(id, 2000);
      if (!rows) {
        console.error(
          `❌ Capture timed out for chart "${id}" — no plotted data (is the chart mounted and loaded?)`,
        );
        return;
      }

      let out = rows;
      if (visibleOnly && Array.isArray(chart.xExtents)) {
        const [s, e] = chart.xExtents;
        const startIdx = Math.max(0, Math.floor(s));
        const endIdx = Math.min(rows.length, Math.ceil(e) + 1);
        out = rows.slice(startIdx, endIdx);
      }

      console.log(
        `📊 ${chart.symbol} ${chart.periodicity} — ${out.length} rows (${visibleOnly ? "visible" : "all"}), ${chart.indicators?.length || 0} indicators`,
      );

      if (!download) return out;

      const base = `${chart.symbol}_${chart.periodicity}_${visibleOnly ? "visible" : "all"}`;
      if (format === "csv") {
        downloadTextFile(`${base}.csv`, rowsToCSV(out), "text/csv");
      } else {
        downloadTextFile(
          `${base}.json`,
          JSON.stringify(
            out,
            (_k, v) => (v instanceof Date ? v.toISOString() : v),
            2,
          ),
          "application/json",
        );
      }
      console.log(`✅ Exported ${base}.${format}`);
      return out;
    } finally {
      window.__CAPTURE_PLOTTED__ = false;
      if (window.__PLOTTED__) delete window.__PLOTTED__[id];
    }
  },

  /**
   * Diff the plotted data of two charts bar-by-bar (aligned by timestamp).
   * Built for the #349 case: chart A (history + realtime) vs chart B (history
   * only) — surfaces exactly which bars/fields diverge. Captures each chart via
   * exportChartData({ download:false }), so it reflects EXACTLY what is rendered.
   *
   * @param {string} chartId1
   * @param {string} chartId2
   * @param {object} [opts]
   * @param {string[]} [opts.fields=['open','high','low','close','volume']]  fields to compare
   * @param {number}   [opts.epsilon=0]       ignore numeric diffs <= this (float tolerance)
   * @param {boolean}  [opts.visibleOnly=false] compare only the on-screen window of each
   * @param {number}   [opts.limit=50]         max rows printed (full list is returned)
   * @returns {Promise<Array>} the divergences
   */
  compareChartData: async (chartId1, chartId2, opts = {}) => {
    const {
      fields = ["open", "high", "low", "close", "volume"],
      epsilon = 0,
      visibleOnly = false,
      limit = 50,
    } = opts;
    if (!window.__CHARTS_STORE__) {
      console.error("❌ Charts store not available (window.__CHARTS_STORE__)");
      return;
    }

    // Capture each chart through the normal export path (returns the rows).
    const a = await devTools.exportChartData(chartId1, {
      download: false,
      visibleOnly,
    });
    const b = await devTools.exportChartData(chartId2, {
      download: false,
      visibleOnly,
    });
    if (!a || !b) {
      console.error(
        "❌ Could not capture one or both charts — see messages above",
      );
      return;
    }

    const num = (v) =>
      v && typeof v === "object" && typeof v.volume === "number" ? v.volume : v;
    const keyOf = (r) =>
      r.date instanceof Date ? r.date.getTime() : new Date(r.date).getTime();
    const mapB = new Map(b.map((r) => [keyOf(r), r]));
    const seenA = new Set();

    const diffs = [];
    for (const ra of a) {
      const k = keyOf(ra);
      seenA.add(k);
      const rb = mapB.get(k);
      if (!rb) {
        diffs.push({
          date: new Date(k).toISOString(),
          field: "(only in chart1)",
          v1: "",
          v2: "",
        });
        continue;
      }
      for (const f of fields) {
        const x = num(ra[f]);
        const y = num(rb[f]);
        if (typeof x === "number" && typeof y === "number") {
          if (Math.abs(x - y) > epsilon) {
            diffs.push({
              date: new Date(k).toISOString(),
              field: f,
              v1: x,
              v2: y,
              delta: x - y,
            });
          }
        } else if (x !== y) {
          diffs.push({
            date: new Date(k).toISOString(),
            field: f,
            v1: x,
            v2: y,
          });
        }
      }
    }
    for (const rb of b) {
      const k = keyOf(rb);
      if (!seenA.has(k)) {
        diffs.push({
          date: new Date(k).toISOString(),
          field: "(only in chart2)",
          v1: "",
          v2: "",
        });
      }
    }
    diffs.sort((p, q) => (p.date < q.date ? -1 : p.date > q.date ? 1 : 0));

    console.log(
      `🔍 chart1=${chartId1} (${a.length} bars) vs chart2=${chartId2} (${b.length} bars) — fields [${fields.join(", ")}], epsilon=${epsilon}`,
    );
    if (diffs.length === 0) {
      console.log("✅ No divergences");
    } else {
      console.warn(`⚠️ ${diffs.length} divergence(s):`);
      console.table(diffs.slice(0, limit));
      if (diffs.length > limit) {
        console.log(
          `… ${diffs.length - limit} more (raise opts.limit; full list returned)`,
        );
      }
    }
    return diffs;
  },

  help: () => {
    console.log("🛠️  Available Dev Tools Commands:");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log(
      "devTools.clearAll()                 - Clear ALL caches (symbols + localStorage)",
    );
    console.log(
      "devTools.clearSymbolsOnly()         - Clear only symbols cache",
    );
    console.log(
      "devTools.clearWorkspace()           - Clear only auto-saved workspace",
    );
    console.log(
      "devTools.cacheInfo()                - Show cache status and info",
    );
    console.log(
      "devTools.freshStart()               - Clear cache + reload (test first load)",
    );
    console.log(
      "devTools.factoryReset()             - 🏭 FACTORY RESET (clear all WITHOUT saving)",
    );
    console.log("");
    console.log("Auto-Save:");
    console.log(
      "devTools.triggerAutoSave()          - 💾 Manually trigger auto-save (for testing)",
    );
    console.log(
      "devTools.triggerRestore()           - 💾 Manually trigger restore (for testing)",
    );
    console.log(
      "devTools.autoSaveInfo()             - 💾 Show auto-save state and details",
    );
    console.log(
      "devTools.lastAutoSaveAttempt()      - 🔍 Show last auto-save attempt (debug navigation)",
    );
    console.log("");
    console.log("What's New:");
    console.log(
      "devTools.resetWhatsNew()            - ✨ Reset What's New (see panel again)",
    );
    console.log(
      'devTools.simulateVersionChange(v)   - ✨ Simulate old version (e.g., "1.0.0")',
    );
    console.log(
      "devTools.whatsNewInfo()             - ✨ Show What's New state",
    );
    console.log("");
    console.log("Tip of the Day:");
    console.log(
      "devTools.resetTipOfTheDay()         - 💡 Reset + show the Tip of the Day now",
    );
    console.log("");
    console.log("Real-time Data:");
    console.log(
      "devTools.quoteSubscriptions()       - 📡 Show quote subscriptions & ref counts",
    );
    console.log(
      "devTools.quoteHealth()              - 🩺 Per-symbol health (staleness + worker + ON)",
    );
    console.log(
      "devTools.showLastQuote(sym)         - 📊 Show all cached fields for a symbol",
    );
    console.log("");
    console.log("Chart Data:");
    console.log(
      "devTools.charts()                   - 📋 List open charts (id, symbol, periodicity, bars, visible)",
    );
    console.log(
      "devTools.exportChartData(id, opts)  - 📤 Export plotted rows (OHLC + indicators) as JSON/CSV",
    );
    console.log(
      "devTools.compareChartData(id1, id2) - 🔍 Diff two charts' plotted bars (e.g. RT vs history-only)",
    );
    console.log(
      "    opts: { visibleOnly=true, format='json'|'csv', download=true }  (id omitted = only open chart)",
    );
    console.log("");
    console.log("");
    console.log("Poll:");
    console.log(
      "devTools.resetPoll(n)               - 🗳️  Reset poll N (deletes vote from server, reload to re-show)",
    );
    console.log(
      "devTools.pollInfo()                  - 🗳️  Show poll state from server",
    );
    console.log("");
    console.log("devTools.help()                     - Show this help");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log(
      "💡 Tip: Use resetWhatsNew() + reload to test What's New panel",
    );
  },

  /**
   * Poll - Reset a specific poll (remove vote from server)
   * @param {number} pollNumber - Poll number to reset (e.g., 1 for poll-1)
   */
  resetPoll: async (pollNumber = 1) => {
    const pollId = `poll-${pollNumber}`;
    console.log(`🗳️  Resetting ${pollId}...`);

    try {
      const data = await marketdata.getPoll();
      if (!data?.votes || data.votes.length === 0) {
        console.log(`ℹ️ No votes found on server`);
        return;
      }

      const before = data.votes.length;
      const filtered = data.votes.filter((v) => v.pollId !== pollId);
      const removed = before - filtered.length;

      if (removed === 0) {
        console.log(`ℹ️ No vote found for ${pollId}`);
        return;
      }

      await marketdata.savePoll({ votes: filtered });

      // Reset local store
      const { default: usePollStore } = await import("../store/pollStore");
      usePollStore.setState({
        hasVoted: false,
        hasCheckedServer: false,
        votes: filtered,
      });

      console.log(
        `✅ Removed ${removed} vote(s) for ${pollId}. Reload to re-show poll.`,
      );
    } catch (error) {
      console.error("❌ Failed to reset poll:", error);
    }
  },

  /**
   * Poll - Show poll state from server
   */
  pollInfo: async () => {
    console.log("🗳️  Poll State:");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    try {
      const data = await marketdata.getPoll();
      if (!data?.votes || data.votes.length === 0) {
        console.log("ℹ️ No votes on server");
      } else {
        console.table(
          data.votes.map((v) => ({
            pollId: v.pollId,
            choice: v.choice,
            date: new Date(v.date).toLocaleString(),
          })),
        );
      }

      const { default: usePollStore } = await import("../store/pollStore");
      const state = usePollStore.getState();
      console.log("Local store:", {
        currentPollId: state.currentPollId,
        hasVoted: state.hasVoted,
        hasCheckedServer: state.hasCheckedServer,
        votesCount: state.votes.length,
      });
    } catch (error) {
      console.error("❌ Failed to get poll info:", error);
    }

    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  },

  // Reporta o protocolo SignalR negociado na conexão /rt (messagepack vs json).
  // Lê direto de marketdata.signalRConnection — sem precisar inspecionar frames.
  protocolInfo: () => {
    const conn = marketdata.signalRConnection;
    if (!conn) {
      console.warn(
        "⚠️ SignalR /rt ainda não conectado (marketdata.signalRConnection ausente)",
      );
      return null;
    }
    const p = conn._protocol || {};
    const info = {
      protocol: p.name ?? "desconhecido",
      version: p.version,
      transferFormat:
        p.transferFormat === 2
          ? "Binary"
          : p.transferFormat === 1
            ? "Text"
            : p.transferFormat,
      connectionState: conn.state,
    };
    console.table(info);
    console.log(
      info.protocol === "messagepack"
        ? "✅ MessagePack ativo (frames binários)"
        : `⚠️ protocolo em uso: ${info.protocol}`,
    );
    return info;
  },
};

// Expose to window for easy browser console access
if (typeof window !== "undefined") {
  window.devTools = devTools;
  console.log('🛠️  Dev Tools loaded! Type "devTools.help()" for commands');
}

export default devTools;
